import Vue from 'vue'
import Router from 'vue-router'
import Meta from 'vue-meta'
import store from '../store/index'
import AuthLayout from '../components/auth/AuthLayout'
import AppLayout from '../components/admin/AppLayout'

Vue.use(Router)

const EmptyParentComponent = {
  template: '<router-view></router-view>',
}

const demoRoutes = []

const router = new Router({
  mode: process.env.VUE_APP_ROUTER_MODE_HISTORY === 'true' ? 'history' : 'history',
  routes: [
    ...demoRoutes,
    {
      path: '*',
      redirect: { name: 'dashboard' },
    },
    {
      path: '/auth',
      component: AuthLayout,
      children: [
        {
          name: 'login',
          path: 'login',
          component: () => import('../components/auth/login/Login.vue'),
        },
        {
          name: 'signup',
          path: 'signup',
          component: () => import('../components/auth/signup/Signup.vue'),
        },
        {
          name: 'recover-password',
          path: 'recover-password',
          component: () => import('../components/auth/recover-password/RecoverPassword.vue'),
        },
        {
          name: 'email-confirmation',
          path: 'email-confirmation/:token',
          component: () => import('../components/auth/email-confirmation/EmailConfirmation.vue'),
        },
        {
          name: 'email-confirmation-prompt',
          path: 'email-confirmation-prompt',
          component: () => import('../components/auth/email-confirmation/EmailConfirmationPrompt.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'recover-password-redirect',
          path: 'recover-password-redirect/:token',
          component: () => import('../components/auth/recover-password/RecoverPasswordRedirect.vue'),
        },
        {
          path: '',
          redirect: { name: 'login' },
        },
      ],
    },
    {
      path: '/404',
      component: EmptyParentComponent,
      children: [
        {
          name: 'not-found-advanced',
          path: 'not-found-advanced',
          component: () => import('../components/pages/404-pages/VaPageNotFoundSearch.vue'),
        },
        {
          name: 'not-found-simple',
          path: 'not-found-simple',
          component: () => import('../components/pages/404-pages/VaPageNotFoundSimple.vue'),
        },
        {
          name: 'not-found-custom',
          path: 'not-found-custom',
          component: () => import('../components/pages/404-pages/VaPageNotFoundCustom.vue'),
        },
        {
          name: 'not-found-large-text',
          path: '/pages/not-found-large-text',
          component: () => import('../components/pages/404-pages/VaPageNotFoundLargeText.vue'),
        },
      ],
    },
    {
      name: 'Admin',
      path: '/admin',
      component: AppLayout,
      children: [
        {
          name: 'dashboard',
          path: 'dashboard',
          component: () => import('../components/dashboard/Dashboard.vue'),
          default: true,
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'profile',
          path: 'profile',
          component: () => import('../components/profile/Profile.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'company',
          path: 'company',
          component: () => import('../components/profile/Company.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'subscriptions',
          path: 'subscriptions',
          component: () => import('../components/subscriptions/Subscriptions.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'payment',
          path: 'payment/subscription/:subscriptionId',
          component: () => import('../components/payment/subscription/PaymentSubscription.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'payment-pack',
          path: 'payment/subscription-pack/:subscriptionPackId',
          component: () => import('../components/payment/subscription/pack/PaymentPack.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'payment-completed',
          path: 'payment-completed/:paymentId',
          component: () => import('../components/payment/PaymentCompleted.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'payment-completed-pack',
          path: 'payment-completed-pack/:paymentId',
          component: () => import('../components/payment/PaymentCompletedPack.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'invoices',
          path: 'invoices',
          component: () => import('../components/invoices/Invoices.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'invoice',
          path: 'invoice/:invoiceId',
          component: () => import('../components/invoices/Invoice.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'app-settings',
          path: 'app-settings',
          component: () => import('../components/appSettings/AppSettings.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'integrations',
          path: 'integrations',
          component: () => import('../components/appSettings/AppThirdPartySettings.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'locations',
          path: 'locations',
          component: () => import('../components/events/Locations.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'location',
          path: 'location/:locationId',
          component: () => import('../components/events/Location.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'new-location',
          path: 'new-location',
          component: () => import('../components/events/NewLocation.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'spots',
          path: 'spots/:classificationId',
          component: () => import('../components/spots/EventSpots.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'spots-raw',
          path: 'spots-raw/:classificationId',
          component: () => import('../components/spots/EventSpotsRaw.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'printers',
          path: 'printers/:locationId',
          component: () => import('../components/printers/LocationPrinters.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'content',
          path: 'content',
          component: () => import('../components/content/Content.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'create-content',
          path: 'create-content',
          component: () => import('../components/content/CreateContent.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'update-content',
          path: 'update-content/:appContentId',
          component: () => import('../components/content/UpdateContent.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'configurations',
          path: 'configurations',
          component: () => import('../components/appConfiguration/AppConfiguration.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'app-subscriptions',
          path: 'app-subscriptions',
          component: () => import('../components/appSubscriptions/AppSubscription.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'create-app-subscription',
          path: 'create-app-subscription/:type',
          component: () => import('../components/appSubscriptions/CreateAppSubscription.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'update-app-subscription',
          path: 'update-app-subscription/:type/:appSubscriptionId',
          component: () => import('../components/appSubscriptions/UpdateAppSubscription.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'app-users',
          path: 'app-users',
          component: () => import('../components/appUsers/AppUsers.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'app-user-timeline',
          path: 'app-user-timeline/:userId',
          component: () => import('../components/appUsers/AppUserTimeline.vue'),
          meta: {
            requiresAuth: true,
          },
        },
        {
          name: 'clients',
          path: 'clients',
          component: () => import('../components/clients/Clients.vue'),
          meta: {
            requiresAuth: true,
          },
        },
      ],
    },
  ],
})

Vue.use(Meta)

router.beforeEach(async (to, from, next) => {
  const appLanguage = localStorage.getItem('appLanguage')
  const code = appLanguage || 'GB'
  if (!store.getters.appTranslations.translation) {
    try {
      await store.dispatch('getAppTranslations', code)
      Vue.i18n.add('GB', store.getters.appTranslations.translation)
      localStorage.setItem('appLanguage', code)
      next()
    } catch (error) {
      console.error('Failed to fetch translations:', error)
    }
  }
  next()
})

router.beforeEach((to, from, next) => {
  if (store.getters.isLoggedIn && !store.getters.isConfirm) {
    if (to.path === '/auth/email-confirmation') {
      store.commit('logout')
      next(to.path)
      return
    }
    if ((from.path === '/auth/login' || from.path === '/auth/signup') && to.path === '/auth/email-confirmation') {
      next('/auth/email-confirmation-prompt')
      return
    }

    if (from.path === '/auth/email-confirmation-prompt' && to.path === '/auth/login') {
      store.commit('logout')
      next('/auth/login')
      return
    }

    if (to.path !== '/auth/email-confirmation-prompt') {
      next('/auth/email-confirmation-prompt')
      return
    }
    // next('/auth/email-confirmation-prompt')
  } else if (to.matched.some(record => record.meta.requiresAuth)) {
    if (store.getters.isLoggedIn) {
      if (((store.getters.localCompanySubscription && store.getters.localCompanySubscription.isExpired) ||
        store.getters.localUserSubscription === null) && to.path !== '/admin/subscriptions') {
        if (to.path === '/admin/profile' ||
          to.path === '/admin/company' ||
          to.path === '/admin/invoices' ||
          to.path.indexOf('/admin/payment-completed') !== -1 ||
          to.path.indexOf('/admin/payment') !== -1) {
          next()
          return
        }
        next('/admin/subscriptions')
        return
      }
      next()
      return
    }
    next('/auth/login')
  } else if (store.getters.isLoggedIn) {
    if (to.path === '/auth/login' || to.path === '/auth/signup') {
      if (to.path === '/auth/login') {
        store.commit('logout')
        next('/auth/login')
        return
      }
      next('/')
    }
  }
  if (to.path === '/auth/email-confirmation') {
    next(to.path)
    return
  }
  next()
})

export default router
